import { useEffect, useState } from 'react';

import {
  useLocation,
  useNavigate
} from 'react-router-dom';

import { API_BASE } from './config';
import { connectSocket } from '../socket/socket';

import '../css/Login.css';


function Login() {

  /* =================================================
     ROUTER
  ================================================= */

  const navigate = useNavigate();
  const location = useLocation();


  /* =================================================
     URL PARAMS
  ================================================= */

  const searchParams =
    new URLSearchParams(
      location.search
    );

  const redirectPath =
    searchParams.get('redirect') || '';

  const playerName =
    searchParams.get('name') || '';

  const safeRedirectPath =
    redirectPath.startsWith('/')
      ? redirectPath
      : '';


  /* =================================================
     STATE
  ================================================= */

  const [form, setForm] =
    useState({
      name: playerName || localStorage.getItem('last_player_id') || '',
      password: ''
    });

  const [loggingIn, setLoggingIn] =
    useState(false);

  const [guideOpen, setGuideOpen] =
    useState(false);

  const [lineLogin, setLineLogin] =
    useState({
      checking: true,
      enabled: false,
      message: ''
    });


  /* =================================================
     LINE LOGIN AVAILABILITY
  ================================================= */

  useEffect(() => {

    let active = true;

    fetch(`${API_BASE}/api/users/line/config`)
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.message || 'ตรวจสอบการเข้าสู่ระบบด้วย LINE ไม่สำเร็จ');
        }
        return data;
      })
      .then(data => {
        if (!active) return;
        setLineLogin({
          checking: false,
          enabled: Boolean(data.enabled),
          message: data.enabled ? '' : 'ผู้ดูแลระบบยังไม่ได้ตั้งค่าการเข้าสู่ระบบด้วย LINE'
        });
      })
      .catch(error => {
        if (!active) return;
        setLineLogin({
          checking: false,
          enabled: false,
          message: error.message || 'การเข้าสู่ระบบด้วย LINE ยังไม่พร้อมใช้งาน'
        });
      });

    return () => {
      active = false;
    };

  }, []);


  const openLineLogin = () => {

    if (!lineLogin.enabled || lineLogin.checking) {
      return;
    }

    const returnPath = safeRedirectPath || '/profile';
    const query = new URLSearchParams({ returnPath });
    window.location.assign(
      `${API_BASE}/api/users/line/login?${query.toString()}`
    );

  };


  /* =================================================
     INPUT
  ================================================= */

  const handleChange = event => {

    const {
      name,
      value
    } = event.target;

    setForm(current => ({
      ...current,
      [name]: value
    }));
  };


  /* =================================================
     PASSWORD LOGIN
  ================================================= */

  const handleSubmit =
    async event => {

      event.preventDefault();

      if (loggingIn) {
        return;
      }

      try {

        setLoggingIn(true);


        const response =
          await fetch(
            `${API_BASE}/api/users/login`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json'
              },

              body:
                JSON.stringify({
                  name:
                    form.name.trim(),

                  password:
                    form.password
                })
            }
          );


        const data =
          await response
            .json()
            .catch(() => ({}));


        if (!response.ok) {

          throw new Error(
            data.message ||
            data.error ||
            'เข้าสู่ระบบไม่สำเร็จ'
          );

        }


        if (
          !data.token ||
          !data.user
        ) {

          throw new Error(
            'ระบบไม่ได้ส่งข้อมูลการเข้าสู่ระบบกลับมาครบ'
          );

        }


        /* ================= SAVE LOGIN ================= */

        localStorage.setItem(
          'token',
          data.token
        );


        localStorage.setItem(
          'user',
          JSON.stringify(
            data.user
          )
        );


        /* ================= SOCKET ================= */

        try {

          connectSocket(
            data.token
          );

        } catch (socketError) {

          console.warn(
            '[LOGIN SOCKET]',
            socketError
          );

        }


        /* =================================================
           ถ้ามาจาก Player History
        ================================================= */

        if (safeRedirectPath) {

          navigate(
            safeRedirectPath,
            {
              replace: true
            }
          );

          return;
        }


        /* =================================================
           LOGIN ปกติ
        ================================================= */

        const role =
          String(
            data.user?.role || ''
          )
            .trim()
            .toLowerCase();


        if (role === 'admin') {

          navigate(
            '/dashboard',
            {
              replace: true
            }
          );

        } else {

          navigate(
            '/profile',
            {
              replace: true
            }
          );

        }


      } catch (error) {

        console.error(
          '[LOGIN ERROR]',
          error
        );


        alert(
          error.message ||
          'เข้าสู่ระบบไม่สำเร็จ'
        );


      } finally {

        setLoggingIn(false);

      }

    };


  /* =================================================
     REGISTER
  ================================================= */

  const openRegister = () => {

    if (safeRedirectPath) {

      navigate(
        `/register?redirect=${encodeURIComponent(
          safeRedirectPath
        )}`
      );

      return;

    }


    navigate(
      '/register'
    );

  };


  /* =================================================
     BACK PLAYER HISTORY
  ================================================= */

  const backToPlayer = () => {

    if (!safeRedirectPath) {
      return;
    }


    navigate(
      safeRedirectPath,
      {
        replace: true
      }
    );

  };


  /* =================================================
     RENDER
  ================================================= */

  return (

    <div className="login-container">

      <div className="login-card">


        {/* ================= LOGO ================= */}

        <div className="login-logo">
          🏸
        </div>


        <h2>
          BADMINTON SPORT
        </h2>


        <p className="login-sub">

          {
            safeRedirectPath
              ? 'เข้าสู่ระบบเพื่อยืนยันตัวตนและเพิ่มรูปผู้เล่น'
              : 'เข้าสู่ระบบเพื่อเริ่มการแข่งขัน'
          }

        </p>

        <button type="button" className="login-guide-button" onClick={() => setGuideOpen(true)}>
          <span>📖</span><span><b>คู่มือเข้าสู่ระบบและสมัครสมาชิก</b><small>กดดูขั้นตอนแบบง่าย ๆ</small></span><i>›</i>
        </button>


        {/* =================================================
           PLAYER HISTORY NOTICE
        ================================================= */}

        {safeRedirectPath && (

          <div
            style={{
              marginBottom: 16,
              padding: '10px 14px',
              borderRadius: 10,

              background:
                'rgba(59,130,246,0.12)',

              border:
                '1px solid rgba(59,130,246,0.3)',

              color:
                '#bfdbfe',

              fontSize: 13
            }}
          >

            🔐 กรุณาเข้าสู่ระบบ
            ด้วยบัญชีของผู้เล่น
            ก่อนเพิ่มรูป

          </div>

        )}


        {/* ================= FORM ================= */}

        <form
          onSubmit={
            handleSubmit
          }

          className="login-form"
        >


          {/* ================= USERNAME ================= */}

          <div className="input-group">

            <label>
              ชื่อผู้เล่น หรือรหัสผู้เล่น
            </label>


            <input
              type="text"

              name="name"

              value={
                form.name
              }

              placeholder="กรอกชื่อ หรือรหัสผู้เล่น"

              onChange={
                handleChange
              }

              autoComplete="
                username
              "

              required
            />

          </div>


          {/* ================= PASSWORD ================= */}

          <div className="input-group">

            <label>
              รหัสผ่าน
            </label>


            <input
              type="password"

              name="password"

              value={
                form.password
              }

              placeholder="
                กรอกรหัสผ่าน
              "

              onChange={
                handleChange
              }

              autoComplete="
                current-password
              "

              required
            />

          </div>


          {/* ================= LOGIN BUTTON ================= */}

          <button
            type="submit"

            className="
              login-btn
            "

            disabled={
              loggingIn
            }
          >

            {
              loggingIn
                ? 'กำลังเข้าสู่ระบบ...'
                : '🔐 เข้าสู่ระบบ'
            }

          </button>

        </form>


        {/* ================= DIVIDER ================= */}

        <div className="login-divider">

          <span>
            หรือ
          </span>

        </div>


        {/* =================================================
           LINE LOGIN
        ================================================= */}

        <button
          type="button"

          className="
            line-login-btn
          "

          disabled={lineLogin.checking || !lineLogin.enabled}

          aria-disabled={lineLogin.checking || !lineLogin.enabled}

          title={lineLogin.message || 'เข้าสู่ระบบด้วยบัญชี LINE'}

          onClick={openLineLogin}
        >

          <span
            className="
              line-login-mark
            "

            aria-hidden={true}
          >

            LINE

          </span>


          <span className="line-login-text">

            <strong>
              เข้าสู่ระบบด้วย LINE
            </strong>

            <small>
              {
                lineLogin.checking
                  ? 'กำลังตรวจสอบบริการ…'
                  : lineLogin.enabled
                    ? 'ครั้งแรกเชื่อมกับรหัสผู้เล่นเพียงครั้งเดียว'
                    : lineLogin.message
              }
            </small>

          </span>

        </button>


        {/* ================= REGISTER ================= */}

        <p className="register-link">

          ยังไม่มีบัญชี?{' '}


          <span
            role="button"

            tabIndex={0}

            onClick={
              openRegister
            }

            onKeyDown={
              event => {

                if (
                  event.key ===
                  'Enter'
                ) {

                  openRegister();

                }

              }
            }
          >

            สมัครสมาชิก

          </span>

        </p>


        {/* ================= MONITOR ================= */}

        <p className="monitor-link">

          🖥️{' '}


          <span
            role="button"

            tabIndex={0}

            onClick={() =>
              navigate(
                '/monitor'
              )
            }

            onKeyDown={
              event => {

                if (
                  event.key ===
                  'Enter'
                ) {

                  navigate(
                    '/monitor'
                  );

                }

              }
            }
          >

            ดูหน้าจอการแข่งขัน

          </span>

        </p>

        {guideOpen && <div className="login-guide-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) setGuideOpen(false); }}>
          <section className="login-guide-modal" role="dialog" aria-modal="true" aria-labelledby="login-guide-title">
            <header><div><span>📖</span><div><h2 id="login-guide-title">เข้าสู่ระบบอย่างไร?</h2><p>เลือกวิธีที่สะดวก ใช้เวลาเพียงไม่กี่ขั้นตอน</p></div></div><button type="button" aria-label="ปิดคู่มือ" onClick={() => setGuideOpen(false)}>×</button></header>
            <div className="login-guide-content">
              <article className="is-line"><div className="login-guide-title"><i>LINE</i><div><h3>เข้าสู่ระบบด้วย LINE</h3><p>เหมาะสำหรับเข้าใช้งานครั้งต่อไปได้รวดเร็ว</p></div></div><ol><li><b>กด “เข้าสู่ระบบด้วย LINE”</b><span>ระบบจะเปิดหน้า LINE เพื่อยืนยันตัวตน</span></li><li><b>ถ้ามีรหัสผู้เล่นอยู่แล้ว</b><span>กรอกรหัสผู้เล่นและรหัสผ่านเพื่อเชื่อมบัญชีครั้งแรก</span></li><li><b>ถ้ายังไม่มีบัญชี</b><span>เลือกสมัครสมาชิก กรอกข้อมูล แล้วระบบจะเชื่อม LINE ให้อัตโนมัติ</span></li><li><b>ครั้งต่อไปกด LINE ได้เลย</b><span>ไม่ต้องกรอกรหัสผู้เล่นซ้ำ</span></li></ol></article>
              <article className="is-id"><div className="login-guide-title"><i>ID</i><div><h3>เข้าสู่ระบบด้วยรหัสผู้เล่น</h3><p>ใช้รหัสผู้เล่นหรือชื่อ พร้อมรหัสผ่าน</p></div></div><ol><li><b>ขอรหัส ID จากผู้จัดก๊วน</b><span>แจ้งผู้จัดก๊วนเพื่อขอรหัสผู้เล่นและรหัสผ่านสำหรับเข้าใช้งาน</span></li><li><b>กรอกชื่อหรือรหัสผู้เล่น</b><span>ใส่ข้อมูลในช่องแรกของหน้า Login</span></li><li><b>กรอกรหัสผ่าน</b><span>ใส่รหัสที่ได้รับจากผู้จัดก๊วน</span></li><li><b>กด “เข้าสู่ระบบ”</b><span>หากเข้าไม่ได้ ให้ตรวจสอบรหัสกับผู้จัดก๊วนอีกครั้ง</span></li></ol></article>
              <article className="is-register"><div className="login-guide-title"><i>สมัคร</i><div><h3>สมัครสมาชิกใหม่</h3><p>สร้างข้อมูลผู้เล่นก่อนเข้าร่วมก๊วน</p></div></div><ol><li><b>กด “สมัครสมาชิก”</b><span>เลือกปุ่มใต้ช่องเข้าสู่ระบบ</span></li><li><b>ตั้งชื่อผู้เล่น</b><span>ใช้ชื่อที่เพื่อนและผู้จัดก๊วนจำได้ง่าย</span></li><li><b>เลือกระดับมือ</b><span>อ่านคำอธิบาย BG, BGM และระดับอื่น แล้วเลือกให้ใกล้เคียงตัวเอง</span></li><li><b>ตั้งรหัสผ่านและกดสมัคร</b><span>จดรหัสผ่านไว้สำหรับ Login ครั้งต่อไป</span></li></ol></article>
              <article className="is-checkin"><div className="login-guide-title"><i>QR</i><div><h3>Check-in เข้าก๊วน</h3><p>ทำหลังจาก Login สำเร็จแล้ว</p></div></div><ol><li><b>มองหา QR ที่หน้าก๊วน</b><span>ผู้จัดก๊วนจะแสดง QR Check-in ไว้ให้สแกน</span></li><li><b>เปิดกล้องและสแกน QR</b><span>ใช้กล้องมือถือสแกนรหัสของก๊วนที่ต้องการเข้า</span></li><li><b>ตรวจสอบชื่อก๊วน</b><span>ยืนยันว่าเป็นก๊วนที่ต้องการเข้าร่วม</span></li><li><b>กด Check-in</b><span>เมื่อสำเร็จ สถานะจะพร้อมให้ผู้จัดนำไปจัด Match</span></li></ol></article>
              <div className="login-guide-note"><span>💡</span><p><b>ยังไม่แน่ใจว่ามีรหัสหรือไม่?</b><br/>สอบถามผู้จัดก๊วนก่อนสมัครใหม่ เพื่อป้องกันข้อมูลผู้เล่นซ้ำ</p></div>
            </div>
            <footer><button type="button" onClick={() => setGuideOpen(false)}>เข้าใจแล้ว กลับหน้า Login</button></footer>
          </section>
        </div>}


        {/* ================= BACK PLAYER ================= */}

        {safeRedirectPath && (

          <p className="monitor-link">

            ↩️{' '}


            <span
              role="button"

              tabIndex={0}

              onClick={
                backToPlayer
              }

              onKeyDown={
                event => {

                  if (
                    event.key ===
                    'Enter'
                  ) {

                    backToPlayer();

                  }

                }
              }
            >

              กลับหน้าประวัติผู้เล่น

            </span>

          </p>

        )}


      </div>

    </div>

  );

}


export default Login;
